import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronDown, ChevronUp, Phone, Mail } from 'lucide-react';

const vendorTypes = [
  { value: 'venue', label: 'Venue / Venue' },
  { value: 'planner', label: 'Wedding planner' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'videographer', label: 'Videograf' },
  { value: 'florist', label: 'Florist' },
  { value: 'catering', label: 'Catering' },
  { value: 'baker', label: 'Baker / Konditor' },
  { value: 'dj', label: 'DJ' },
  { value: 'band', label: 'Band / Musiciane' },
  { value: 'hair', label: 'Hair' },
  { value: 'makeup', label: 'Makeup' },
  { value: 'officiant', label: 'Officiant / Offisiant' },
  { value: 'transport', label: 'Transport' },
  { value: 'rentals', label: 'Utleie / Dekor' },
  { value: 'stationery', label: 'Stationery / Trykk' },
  { value: 'entertainment', label: 'Underholdning' },
  { value: 'childcare', label: 'Childevakt' },
  { value: 'other', label: 'Other' },
];

const defaultVendor = {
  type: '', name: '', contactPerson: '', phone: '', email: '',
  hasContract: false, totalCost: '', deposit: '', depositPaid: false,
  paid: '', deadline: '', arrivalTime: '', setupTime: '',
  emergencyContact: '', notes: '',
};

export const VendorList = ({ vendors, onChange }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const addVendor = () => {
    onChange([...vendors, { ...defaultVendor }]);
    setExpandedIdx(vendors.length);
  };
  const removeVendor = (idx) => onChange(vendors.filter((_, i) => i !== idx));
  const updateVendor = (idx, field, value) => {
    const updated = [...vendors];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  return (
    <div data-testid="vendor-list" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Vendors</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Keep track of all vendors</p>
          <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
            Add vendors with contact info, contract status, costs and payment deadlines. Costs sync to the budget module.
          </p>
        </div>
        <Button type="button" onClick={addVendor} variant="outline" size="sm" className="rounded-full text-xs font-sans gap-1.5 border-stone-200">
          <Plus className="w-3.5 h-3.5" /> Add vendor
        </Button>
      </div>

      <div className="space-y-2">
        {vendors.map((vendor, i) => {
          const expanded = expandedIdx === i;
          const typeLabel = vendorTypes.find(t => t.value === vendor.type)?.label || 'Vendor';
          return (
            <div key={i} className={`border rounded-xl transition-all ${expanded ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
              <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedIdx(expanded ? null : i)}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-sans font-medium text-stone-800">{vendor.name || `${typeLabel} ${i + 1}`}</p>
                  <p className="text-xs font-sans text-stone-400">{typeLabel}{vendor.totalCost ? ` · ${parseInt(vendor.totalCost).toLocaleString('en-US')} kr` : ''}</p>
                </div>
                {vendor.hasContract && <span className="text-[10px] font-sans font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Kontrakt</span>}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500"
                  onClick={e => { e.stopPropagation(); removeVendor(i); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
              </div>

              {expanded && (
                <div className="px-3 pb-3 border-t border-stone-100 space-y-3 pt-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Type</label>
                      <select value={vendor.type} onChange={e => updateVendor(i, 'type', e.target.value)}
                        className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none appearance-none">
                        <option value="">Select type</option>
                        {vendorTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <Inp label="Firmanavn" value={vendor.name} onChange={v => updateVendor(i, 'name', v)} placeholder="Firmanavn" />
                    <Inp label="Contact person" value={vendor.contactPerson} onChange={v => updateVendor(i, 'contactPerson', v)} placeholder="Name" />
                    <Inp label="Phone" value={vendor.phone} onChange={v => updateVendor(i, 'phone', v)} placeholder="Phone" />
                    <Inp label="Email" value={vendor.email} onChange={v => updateVendor(i, 'email', v)} placeholder="Email" />
                    <Inp label="Emergency contact" value={vendor.emergencyContact} onChange={v => updateVendor(i, 'emergencyContact', v)} placeholder="Phone" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Inp label="Totalpris (kr)" value={vendor.totalCost} onChange={v => updateVendor(i, 'totalCost', v)} type="number" />
                    <Inp label="Depositum (kr)" value={vendor.deposit} onChange={v => updateVendor(i, 'deposit', v)} type="number" />
                    <Inp label="Paid (kr)" value={vendor.paid} onChange={v => updateVendor(i, 'paid', v)} type="number" />
                    <Inp label="Payment deadline" value={vendor.deadline} onChange={v => updateVendor(i, 'deadline', v)} type="date" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Inp label="Arrivaltid" value={vendor.arrivalTime} onChange={v => updateVendor(i, 'arrivalTime', v)} placeholder="e.g. 12:00" />
                    <Inp label="Setup-tid" value={vendor.setupTime} onChange={v => updateVendor(i, 'setupTime', v)} placeholder="e.g. 2 timer" />
                    <label className="flex items-center gap-2 pt-4">
                      <input type="checkbox" checked={vendor.hasContract} onChange={e => updateVendor(i, 'hasContract', e.target.checked)}
                        className="rounded border-stone-300 text-primary" />
                      <span className="text-xs font-sans text-stone-600">Kontrakt signert</span>
                    </label>
                  </div>
                  <Inp label="Notes" value={vendor.notes} onChange={v => updateVendor(i, 'notes', v)} placeholder="Andre merknader" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {vendors.length === 0 && (
        <button type="button" onClick={addVendor}
          className="w-full py-8 border-2 border-dashed border-stone-200 rounded-2xl hover:border-primary/40 transition-colors text-center">
          <Phone className="w-7 h-7 text-stone-300 mx-auto mb-2" />
          <p className="text-sm font-sans text-stone-500">Add your first vendor</p>
        </button>
      )}
    </div>
  );
};

const Inp = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-[10px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);
